import type { ThemeDescriptor } from "@/lib/api";
import { thumbSpec } from "@/lib/themes/look";
import { testIds } from "@/testIds";

interface ThemeThumbnailProps {
  theme: ThemeDescriptor;
  className?: string;
  /** The small page (~32 px tall) for list rows and "Used in": thinner bars, fewer lines. */
  compact?: boolean;
}

/**
 * A schematic page drawn from the theme descriptor (handoff 1e, 2c): paper,
 * a kicker tab, two heading bars and text lines; Index adds a spine band of
 * its first six chapter colours. Drawn, not rendered — no compile, and it
 * never shows a document's content, so it is safe anywhere.
 */
export function ThemeThumbnail({ theme, className, compact = false }: ThemeThumbnailProps) {
  const spec = thumbSpec(theme);
  if (compact) {
    return (
      <div
        data-testid={testIds.look.thumb}
        data-theme={theme.id}
        aria-hidden="true"
        className={`relative overflow-hidden rounded-[3px] flex flex-col gap-[2px] px-[4px] pt-[5px] ${className ?? ""}`}
        style={{ background: spec.paper, boxShadow: "inset 0 0 0 1px rgba(16,24,40,.14)" }}
      >
        <div data-part="kicker" className="h-[2px] w-[45%] rounded-[1px]" style={{ background: spec.kicker }} />
        <div data-part="heading" className="h-[2.5px] w-[75%] rounded-[1px]" style={{ background: spec.heading }} />
        <div className="mt-[1px] h-[1.5px]" style={{ background: spec.line }} />
        <div className="h-[1.5px] w-[80%]" style={{ background: spec.line }} />
        <div className="h-[1.5px]" style={{ background: spec.line }} />
        {spec.band && (
          <div data-part="band" className="absolute right-0 top-0 bottom-0 w-[3px] flex flex-col">
            {spec.band.map((c, i) => (
              <div key={i} data-band-colour={c} className="flex-1" style={{ background: c }} />
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      data-testid={testIds.look.thumb}
      data-theme={theme.id}
      aria-hidden="true"
      className={`relative overflow-hidden rounded-[5px] flex flex-col gap-[4px] px-2 pt-2.5 ${className ?? ""}`}
      style={{ background: spec.paper, boxShadow: "inset 0 0 0 1px rgba(16,24,40,.08)" }}
    >
      <div data-part="kicker" className="h-[4px] w-[40%] rounded-[2px]" style={{ background: spec.kicker }} />
      <div data-part="heading" className="mt-[2px] h-[5px] w-[72%] rounded-[2px]" style={{ background: spec.heading }} />
      <div data-part="heading2" className="h-[5px] w-[50%] rounded-[2px]" style={{ background: spec.heading2 }} />
      <div className="mt-[4px] h-[2px]" style={{ background: spec.line }} />
      <div className="h-[2px]" style={{ background: spec.line }} />
      <div className="h-[2px] w-[85%]" style={{ background: spec.line }} />
      <div className="h-[2px]" style={{ background: spec.line }} />
      <div className="h-[2px] w-[70%]" style={{ background: spec.line }} />
      {spec.band && (
        <div data-part="band" className="absolute right-0 top-0 bottom-0 w-[5px] flex flex-col">
          {spec.band.map((c, i) => (
            <div key={i} data-band-colour={c} className="flex-1" style={{ background: c }} />
          ))}
        </div>
      )}
    </div>
  );
}
